import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useHostBranding } from "@/components/HostBranding";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, Sun, Moon } from "lucide-react";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useTheme } from "next-themes";
import { trackEvent, ConversionEvents } from "@/lib/analytics";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { shopUrl } from "@/config/platform";
import PlatformLink from "@/components/PlatformLink";
import { useMarketingTone } from "@/components/MarketingToneContext";
import '@/pages/landing-animations.css';

// Top-of-funnel CTA — the global "Start for free" / "Sign up" button.
// Without this event we can't tell which surface (header, blog, pricing)
// brings visitors to /auth.
function trackHeaderCta(label: string, target: string) {
  trackEvent(ConversionEvents.CTA_CLICKED, {
    source: 'header',
    label: label.slice(0, 64),
    href: target.slice(0, 200),
    path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

export const MarketingHeader = ({ dashboardMode, onOpenSettings, onOpenSupport, tone: toneProp }: {
  dashboardMode?: boolean;
  onOpenSettings?: () => void;
  onOpenSupport?: () => void;
  /** Surface the header sits on. Defaults to the tone RootLayout published
   *  for the shell (light on every marketing route): <html> carries the app's
   *  forced `dark` class, so the theme-driven logo swap below would otherwise
   *  show the light-on-dark wordmark on a white header. */
  tone?: 'light' | 'dark';
} = {}) => {
  const shellTone = useMarketingTone();
  const tone = toneProp ?? shellTone;
  const { user, signOut } = useAuth();
  const brand = useHostBranding(); // non-null on a customer's branded host → swap our logo for theirs
  const { t } = useTranslation(['translation', 'common']);
  const { theme, resolvedTheme, setTheme } = useTheme();
  const lp = useLocalizedPath();

  const toggleTheme = () => {
    const isDark = resolvedTheme === 'dark';
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('todaidesign_theme_chosen', next);
  };
  const [mounted, setMounted] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [disciplinesOpen, setDisciplinesOpen] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = [
    { to: lp("/calculators"), label: t('calculators_nav'), badge: 'Free' },
    // The store is the other company's site: an absolute URL on todae.net,
    // never the /todae mount inside this build (PlatformLink renders an <a>).
    { to: shopUrl(), label: t('store_nav', 'Store') },
    { to: lp("/pricing"), label: t('pricing') },
    { to: lp("/docs"), label: t('docs_nav') },
  ];

  return (
    <>
      <header className="pelco-header">
        <div className="pelco-container h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo — brand refresh V4 horizontal lockup (mark + wordmark
                with .io accent). Light/dark variants swapped via CSS visibility. */}
            <Link to={lp("/")} className="flex-shrink-0 flex items-center" aria-label={`${brand?.name || 'TodAidesign'} home`}>
              {brand ? (
                brand.logoUrl
                  ? <img src={brand.logoUrl} alt={brand.name} className="h-9 w-auto block object-contain" />
                  : <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.01em', color: 'var(--color-gray-dark)' }}>{brand.name}</span>
              ) : tone ? (
                <img
                  src={tone === 'light' ? '/logos/logo-horizontal-dark.svg' : '/logos/logo-horizontal-light.svg'}
                  alt="TodAidesign"
                  className="h-9 w-auto block"
                  width={460}
                  height={120}
                />
              ) : (
                <>
                  <img src="/logos/logo-horizontal-dark.svg" alt="TodAidesign" className="h-9 w-auto block dark:hidden" />
                  <img src="/logos/logo-horizontal-light.svg" alt="TodAidesign" className="h-9 w-auto hidden dark:block" />
                </>
              )}
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <PlatformLink
                  key={link.to}
                  href={link.to}
                  className="pelco-nav-link flex items-center gap-1.5"
                >
                  {link.label}
                  {link.badge && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(91, 140, 255, 0.15)', color: 'var(--color-brand-blue)' }}>
                      {link.badge}
                    </span>
                  )}
                </PlatformLink>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <div className="hidden lg:flex items-center gap-2">
                <LanguageSelector />
              </div>

              {mounted && user ? (
                <div className="hidden lg:flex items-center gap-3">
                  {dashboardMode ? (
                    <>
                      <button onClick={() => signOut()} className="pelco-nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                        {t('common:logout', { defaultValue: 'Log out' })}
                      </button>
                      {onOpenSupport && (
                        <button onClick={onOpenSupport} className="pelco-btn pelco-btn--outline" style={{ padding: '0.5rem 1.25rem', fontSize: '0.8125rem' }}>
                          Support
                        </button>
                      )}
                      {onOpenSettings && (
                        <button onClick={onOpenSettings} className="pelco-btn pelco-btn--primary" style={{ padding: '0.5rem 1.5rem', fontSize: '0.8125rem' }}>
                          {t('dashboard:settings_btn', { defaultValue: 'Settings' })}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <Link to="/dashboard" className="pelco-nav-link">
                        {t('common:dashboard')}
                      </Link>
                      <Link to="/cctv-design" className="pelco-btn pelco-btn--primary" style={{ padding: '0.5rem 1.5rem', fontSize: '0.8125rem' }}>
                        {t('hero2_cta_primary', { defaultValue: 'Open the designer' })}
                      </Link>
                    </>
                  )}
                </div>
              ) : (
                <div className="hidden lg:flex items-center gap-3">
                  <Link to="/auth" className="pelco-nav-link">
                    {t('common:login')}
                  </Link>
                  {!brand && (
                  <Link to="/auth?mode=signup&redirect=/cctv-design" className="pelco-btn pelco-btn--primary" style={{ padding: '0.5rem 1.5rem', fontSize: '0.8125rem' }}
                    onClick={() => trackHeaderCta(t('plan_cta_get_started', { defaultValue: 'Start for free' }), '/auth?mode=signup')}>
                    {t('plan_cta_get_started', { defaultValue: 'Start for free' })}
                  </Link>
                  )}
                </div>
              )}

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 active:scale-95 active:opacity-70"
                style={{ color: 'var(--color-gray-dark)' }}
                aria-label={t('common:open_menu')}
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-0 end-0 bottom-0 w-[300px] max-w-[85vw] flex flex-col animate-in slide-in-from-right rtl:slide-in-from-left" style={{ backgroundColor: 'var(--color-page-bg)' }}>
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 border-b" style={{ height: 'var(--header-height)', borderColor: 'var(--color-gray-border)', paddingTop: 'env(safe-area-inset-top)' }}>
              <div className="flex items-center gap-2">
                {brand ? (
                  <>
                    {brand.logoUrl && <img src={brand.logoUrl} alt="" className="h-7 w-auto object-contain" />}
                    <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.01em', color: 'var(--color-gray-dark)' }}>
                      {brand.name}
                    </span>
                  </>
                ) : (
                  <>
                    {/* Mark-only reads cleanly on the drawer's page-bg; variants swap by
                        theme unless the page names its surface. */}
                    {tone ? (
                      <img src={tone === 'light' ? '/logos/logo-mark.svg' : '/logos/logo-mark-on-dark.svg'} alt="" className="w-7 h-7 block" />
                    ) : (
                      <>
                        <img src="/logos/logo-mark.svg" alt="" className="w-7 h-7 block dark:hidden" />
                        <img src="/logos/logo-mark-on-dark.svg" alt="" className="w-7 h-7 hidden dark:block" />
                      </>
                    )}
                    <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.01em', color: 'var(--color-gray-dark)' }}>
                      TodAidesign
                    </span>
                  </>
                )}
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-2 -me-2" style={{ color: 'var(--color-gray-muted)' }} aria-label={t('common:close_menu')}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer links */}
            <nav className="flex-1 overflow-y-auto px-5 py-6 space-y-1" style={{ fontFamily: 'var(--font-ui), sans-serif' }}>
              {navLinks.map((link) => (
                <PlatformLink key={link.to} href={link.to} onClick={() => setMobileOpen(false)}
                  className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                  {link.label}
                </PlatformLink>
              ))}

              {mounted && user && (
                <>
                  <div className="my-3" style={{ borderTop: '1px solid var(--color-gray-border)' }} />
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)}
                    className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                    {t('common:dashboard')}
                  </Link>
                  {dashboardMode && onOpenSettings && (
                    <button
                      onClick={() => { onOpenSettings(); setMobileOpen(false); }}
                      className="block py-3 text-base font-medium w-full text-start"
                      style={{ color: 'var(--color-gray-dark)' }}
                    >
                      {t('dashboard:settings_btn', { defaultValue: 'Settings' })}
                    </button>
                  )}
                  <button onClick={() => { signOut(); setMobileOpen(false); }}
                    className="block py-3 text-base font-medium text-red-500 w-full text-start">
                    {t('common:logout', { defaultValue: 'Log out' })}
                  </button>
                </>
              )}

              {mounted && !user && (
                <>
                  <div className="my-3" style={{ borderTop: '1px solid var(--color-gray-border)' }} />
                  <Link to="/auth" onClick={() => setMobileOpen(false)}
                    className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-muted)' }}>
                    {t('common:login')}
                  </Link>
                  <Link to="/auth?mode=signup&redirect=/cctv-design"
                    onClick={() => {
                      trackHeaderCta(t('plan_cta_get_started', { defaultValue: 'Start for free' }), '/auth?mode=signup');
                      setMobileOpen(false);
                    }}
                    className="block mt-2 py-3 text-center text-base font-bold bg-[var(--color-brand-blue)] text-white">
                    {t('plan_cta_get_started', { defaultValue: 'Start for free' })}
                  </Link>
                </>
              )}

              <div className="pt-4 flex items-center gap-3">
                <LanguageSelector />
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};
